import { Button } from '@/components/ui';
import { Heading, Paragraph } from '@/components/ui/Typography';
import { useCreateProgramme, useUpdateProgramme } from '@/lib/api/hooks/programme';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import { showToast } from '@/lib/toast';
import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { IProgrammeMultiStepFormValues } from '@/pages/articles-pages/programmes/MultiStepForm/MultiStepForm';
import TemplatePreviewCard from '@/pages/articles-pages/programmes/MultiStepForm/MicroSite/TemplatePreviewCard';
import micrositeTemplatesData from "@/pages/articles-pages/programmes/MultiStepForm/MicroSite/micrositeTemplateTypesData";


interface ReviewProps {
  formValues: IProgrammeMultiStepFormValues;
  prevStep: () => void;
  goToPage: (page: number) => void;
}

const Review = ({
  formValues,
  prevStep,
  goToPage
}: ReviewProps) => {
  const navigate = useNavigate();

      const { id } = useParams<{ id?: string }>();
    const isEditMode = Boolean(id && location.pathname.includes('/edit'));
    const programmeId = isEditMode ? id : '';

  const [isSaving, setIsSaving] = useState(false);
  const { mutateAsync: uploadFile } = useFileUpload();
  const { mutate: createProgramme } = useCreateProgramme();
  const { mutate: updateProgramme } = useUpdateProgramme();



  const selectedMicrositeTemplateType = formValues.microSiteRequest && typeof formValues.microSiteRequest === 'object'
    ? formValues.microSiteRequest.micrositeTemplateType || ''    : '';
    const coverImageFile = formValues.localCoverImageFile;
    const coverImagePreviewUrl = coverImageFile ? URL.createObjectURL(coverImageFile) : formValues.coverImageUrl || '';
    const isMicrositeEnabled = formValues.isAddWebsite && formValues.websiteType === 'MICROSITE';

    const templatePreviewData = micrositeTemplatesData.find(template => template.id.toLowerCase() === selectedMicrositeTemplateType.toLowerCase()) || null;

  const handleSubmit = async (status: 'DRAFT' | 'PUBLISH') => {
    setIsSaving(true);

    let finalCoverImageUrl = formValues.coverImageUrl;

    if (coverImageFile) {
      try {
        const uploaded = await uploadFile({ file: coverImageFile, category: 'IMAGE' });
        finalCoverImageUrl = uploaded?.data?.fileUrl || '';
      } catch (error) {
        console.error(error);
        showToast.error('Cover image upload failed. Please try again.');
        setIsSaving(false);
        return;
      }
    }

    if (!finalCoverImageUrl) {
      showToast.error('Please add a cover image in the main form before submitting.');
      setIsSaving(false);
      return;
    }

    const articleTagArray = formValues.articleTag
      ? formValues.articleTag.split(',').map((tag) => tag.trim()).filter(Boolean)
      : [];

    const payload = {
      title: formValues.title,
      coverImageTitle: formValues.coverImageTitle,
      articleTag: articleTagArray,
      allowComments: Boolean(formValues.allowComments),
      content: formValues.content,
      status,
      coverImageUrl: finalCoverImageUrl,
      websiteType: formValues.websiteType,
      websiteLink: formValues.websiteLink,
      isAddWebsite: formValues.isAddWebsite,
      microSiteRequest: formValues.microSiteRequest,
    };

    if (isEditMode && programmeId) {
      updateProgramme(
        { programmeId, data: payload },
        {
          onSuccess: (response) => {
            setIsSaving(false);
            if (!response.success) return;

            if (status === 'PUBLISH') {
              showToast.publishSuccess({
                title: 'Publish Successful',
                message: 'You have successfully published your programme',
                onGoHome: () => navigate('/admin/programmes'),
              });
            } else {
              showToast.success('Programme updated successfully!');
              navigate('/admin/programmes');
            }
          },
          onError: (error: Error) => {
            setIsSaving(false);
            showToast.error(`Failed to update programme: ${error.message || 'Unknown error'}`);
          },
        },
      );
      return;
    }

    createProgramme(payload, {
      onSuccess: (response) => {
        setIsSaving(false);
        if (!response.success) return;

        if (status === 'PUBLISH') {
          showToast.publishSuccess({
            title: 'Publish Successful',
            message: 'You have successfully published your programme',
            onGoHome: () => navigate('/admin/programmes'),
          });
        } else {
          showToast.success('Programme created successfully!');
          navigate('/admin/programmes');
        }
      },
      onError: (error: Error) => {
        setIsSaving(false);
        showToast.error(`Failed to create programme: ${error.message || 'Unknown error'}`);
      },
    });
  };

  return (
    <div className="flex flex-col gap-6 rounded-xl bg-white p-6">
            <div className="flex items-center sticky top-0 z-10 justify-between border-b pb-4 border-gray-200 bg-white">
              <div className="flex gap-3 items-center">
    <Button type="button" variant="secondary" onClick={prevStep} disabled={isSaving}>
          Edit Microsite
        </Button>
            <Button type="button" variant="secondary" onClick={() => goToPage(1)} disabled={isSaving}>
          Edit Main Form
        </Button>
              </div>
    

        <div className="flex items-center gap-3">
          <Button type="button" variant="secondary" onClick={() => handleSubmit('DRAFT')} disabled={isSaving}>
            Save Draft
          </Button>
          <Button type="button" onClick={() => handleSubmit('PUBLISH')} loading={isSaving}>
            Publish
          </Button>
        </div>
      </div>
   
        <Heading level={4}>Review</Heading>
   
      <div className="space-y-4 rounded-lg border border-gray-200 p-4">
        <div>
          <Paragraph className="text-xs font-semibold uppercase tracking-wide text-gray-500">Title</Paragraph>
          <Paragraph>{formValues.title || '-'}</Paragraph>
        </div>

        <div>
          <Paragraph className="text-xs font-semibold uppercase tracking-wide text-gray-500">Article Tags</Paragraph>
          <Paragraph>{formValues.articleTag || '-'}</Paragraph>
        </div>

        <div>
          <Paragraph className="text-xs font-semibold uppercase tracking-wide text-gray-500">Allow Comments</Paragraph>
          <Paragraph>{formValues.allowComments ? 'Yes' : 'No'}</Paragraph>
        </div>

        <div>
          <Paragraph className="text-xs font-semibold uppercase tracking-wide text-gray-500">Website Setup</Paragraph>
          <Paragraph>
            {formValues.isAddWebsite
              ? formValues.websiteType === 'MICROSITE'
                ? 'Microsite selected'
                : `Website link: ${formValues.websiteLink || '-'}`
              : 'No website attached'}
          </Paragraph>
        </div>

        <div>
          <Paragraph className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Content</Paragraph>
          <div className="rounded-md border border-gray-100 bg-gray-50 p-4">
            {formValues.content ? (
              <div dangerouslySetInnerHTML={{ __html: formValues.content }} />
            ) : (
              <Paragraph>-</Paragraph>
            )}
          </div>
        </div>
      </div>

<div className=" rounded-lg border border-gray-200 p-4 grid grid-cols-1 md:grid-cols-2 gap-6">


      <div className="h-full flex flex-col">
        <Paragraph className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Cover Image</Paragraph>
      
          <div className="overflow-hidden flex-1 rounded-lg border border-gray-100">
            <img src={coverImagePreviewUrl} alt={formValues.coverImageTitle || 'Cover image'} className="h-full w-full object-cover" />
        
          </div>
            <Paragraph className="mt-2 text-sm text-gray-500">{formValues.coverImageTitle || 'Cover image'}</Paragraph>
      </div>

      {isMicrositeEnabled && selectedMicrositeTemplateType && templatePreviewData ? (
        <div className="h-full flex flex-col">
          <Paragraph className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Microsite Preview</Paragraph>
           <TemplatePreviewCard template={templatePreviewData} readOnly />
        </div>
      ) : null}

</div>
    </div>
  );
};

export default Review;